// =============================================================================
// Decision-logic spec — TypeScript mirror of shared/decision-logic.yaml
//
// This file is the runtime representation of the spec the engine evaluates
// against. It's hand-transcribed from shared/decision-logic.yaml to keep
// the runtime bundle small and strongly typed without a codegen step.
//
// Drift guard: tests/spec-sync.test.ts loads the YAML at test time and
// verifies every value here matches the YAML. Breaks CI if they diverge.
//
// When editing: always change the YAML first, then update this file to match,
// then run the test to confirm.
// =============================================================================

import type { EvidenceBadge } from './types.js';

/** Metadata from the spec. */
export const SPEC_METADATA = {
  version: '0.1.1',
  date: '2026-04-22',
  sourceManifest: 'coverage-manifest.md',
} as const;

// -----------------------------------------------------------------------------
// LAYER 1 — THRESHOLDS
// Numbers per marker + life stage, tagged to their evidence strength.
// -----------------------------------------------------------------------------

/** Ferritin thresholds (μg/L) by life stage. */
export const FERRITIN_THRESHOLDS = {
  non_pregnant_premenopausal: {
    stage0ReleteMin: 50,
    stage1IncipientLower: 30,
    stage2DepletionLower: 25,
    stage3IDEUpper: 25,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['addo-2025', 'mei-2021', 'galetti-2021'],
    manifestRef: ['A1', 'A2'],
  },
  pregnant_t1: {
    stage3IDEUpper: 25.8,
    adequateReserveMin: 60,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['mei-2025', 'mccarthy-2024', 'bothwell-2000'],
    manifestRef: ['A4'],
  },
  pregnant_t2: {
    stage3IDEUpper: 18.3,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['mei-2025'],
    manifestRef: ['A5'],
  },
  pregnant_t3: {
    stage3IDEUpper: 19.0,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['mei-2025'],
    manifestRef: ['A6'],
  },
  postpartum_within_6mo: {
    stage3IDEUpper: 30,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['milman-2011', 'butwick-2021', 'akesson-2002'],
    manifestRef: ['A7'],
  },
  perimenopausal: {
    stage3IDEUpper: 25,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['kim-2025', 'parker-2020', 'means-2024', 'merlo-2023'],
    manifestRef: ['A8'],
  },
  postmenopausal: {
    stage3IDEUpperAtAge70plus: 38,
    stage3IDEUpperAtAge50: 22,
    hematologicResponseThreshold: 25,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['means-2024', 'foy-2019', 'kim-2025', 'merlo-2023', 'warne-2017', 'wouters-2019'],
    manifestRef: ['A9'],
  },
} as const;

/** Overload evaluation triggers (ferritin + TSAT combined). */
export const OVERLOAD_TRIGGERS = {
  premenopausal: {
    ferritinGt: 200,
    tsatGt: 45,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
    primaryCitations: ['easl-2022', 'adams-2005', 'allen-2008'],
    manifestRef: ['A10', 'I3'],
  },
  postmenopausal: {
    ferritinGt: 300,
    tsatGt: 50,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
    primaryCitations: ['easl-2022', 'adams-2005'],
    manifestRef: ['A10', 'I3'],
  },
  anyStageRegardlessOfTSAT: {
    ferritinGt: 1000,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['waalen-2008', 'adams-2005'],
    manifestRef: ['A10'],
    note: 'Only ~8% of such cases are hemochromatosis; most are alcohol, liver disease, metabolic syndrome, or malignancy',
  },
  sensitiveScreeningTriggerFemale: {
    tsatGt: 32,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['lim-2014', 'lou-2024'],
    note: 'Below formal EASL threshold; for physician-conversation trigger, not diagnosis',
  },
} as const;

/** Hemoglobin anemia thresholds (internal units g/L). */
export const HEMOGLOBIN_THRESHOLDS = {
  non_pregnant_premenopausal: {
    anemia: 120,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
    primaryCitations: ['braat-2024', 'addo-2019'],
    manifestRef: ['B1'],
  },
  pregnant_t1: {
    anemia: 110,
    predictsT3AnemiaThreshold: 126.5,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
    primaryCitations: ['braat-2024', 'young-2023'],
    manifestRef: ['B2'],
  },
  pregnant_t2: {
    anemia: 105,
    shinarROCOptimalSingleton: 100,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
    primaryCitations: ['braat-2024', 'young-2023', 'shinar-2018'],
    manifestRef: ['B3'],
  },
  pregnant_t3: {
    anemia: 100,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
    primaryCitations: ['young-2023'],
    manifestRef: ['B4'],
  },
  postpartum_1_week: {
    anemia: 110,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['milman-2011'],
    manifestRef: ['B5', 'A7'],
  },
  postpartum_8_weeks: {
    anemia: 120,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['milman-2011'],
    manifestRef: ['B5', 'A7'],
  },
  postmenopausal: {
    whoThreshold: 120,
    evidenceSuggestsRaisedTo: 130,
    strength: 'PRELIMINARY' as EvidenceBadge,
    primaryCitations: ['wouters-2019', 'netz-2024'],
    manifestRef: ['B6', 'A9'],
  },
  maleAdult: {
    anemia: 130,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
  },
} as const;

/** Severity thresholds — drive the urgent/prompt/routine banner. */
export const SEVERITY_THRESHOLDS = {
  mildNonPregnantRange: [100, 119] as const,
  mildPregnantRange: [100, 109] as const,
  moderateAnyRange: [70, 99] as const,
  severeThreshold: 70, // Hb < 7.0 g/dL → urgent
  severePostpartumThreshold: 80, // Hb < 8.0 g/dL postpartum → urgent
  transfusionTerritory: 50,
  strength: 'HIGH_QUALITY' as EvidenceBadge,
  badgeOnGuideline: 'CONSENSUS' as EvidenceBadge,
  primaryCitations: ['mansukhani-2023', 'butwick-2016'],
  manifestRef: ['B7'],
} as const;

/** TSAT thresholds (percent). */
export const TSAT_THRESHOLDS = {
  lowThreshold: 20,
  normalRangeLow: 20,
  normalRangeHigh: 45,
  overloadTriggerPremenopausal: 45,
  overloadTriggerPostmenopausal: 50,
  screeningTriggerFemaleSensitive: 32,
  strength: 'HIGH_QUALITY' as EvidenceBadge,
  primaryCitations: ['easl-2022', 'cacoub-2019', 'lim-2014'],
  manifestRef: ['C', 'A10', 'I3'],
} as const;

/** Red cell indices. */
export const MCV_THRESHOLDS = {
  microcyticThresholdFemale: 80,
  macrocyticThreshold: 100,
  strength: 'HIGH_QUALITY' as EvidenceBadge,
} as const;

export const RDW_THRESHOLDS = {
  elevatedThreshold: 14.5,
  strength: 'HIGH_QUALITY' as EvidenceBadge,
  primaryCitations: ['van-zeben-1990'],
  manifestRef: ['I4'],
} as const;

/** Inflammation markers. */
export const INFLAMMATION_THRESHOLDS = {
  crp: {
    inflammationThreshold: 5,
    highInflammationThreshold: 10,
    strength: 'HIGH_QUALITY' as EvidenceBadge,
    primaryCitations: ['namaste-2017', 'thurnham-2010', 'williams-2019', 'ko-2024'],
    manifestRef: ['D1', 'D2', 'D3'],
  },
} as const;

// -----------------------------------------------------------------------------
// Re-exports for convenience
// -----------------------------------------------------------------------------

export type LifeStageKey = keyof typeof FERRITIN_THRESHOLDS;
